import { useEffect, useMemo, useState } from "react";

import {
  getNodeOutputModel,
  ModelNotFoundException,
  type NodeModelWithId,
  type OutputModelFetcher,
  ReferencedNodeNotFoundException,
} from "@doitintl/cloudflow-commons";
import {
  CloudFlowNodeType,
  type NodeTransitionList,
  type UnwrappedApiServiceModelDescriptor,
} from "@doitintl/cmp-models";
import { type WithModel } from "@doitintl/models-any";
import { type Node, useReactFlow } from "@xyflow/react";

import { type NodeModelWithIdAndStep, type RFNode } from "../../../types";
import { useVariableContext } from "../../Common/VariableProvider";
import { getOutputModelBySystemEventTriggerNode } from "../utils/systemEventTriggerModel";
import { getOutputModelByOperationPointer } from "./useUnwrappedApiActionModel";

export type NodeWitOutputModel = {
  id: string;
  name: string;
  type: CloudFlowNodeType;
  stepNumber: number;
  outputModel: UnwrappedApiServiceModelDescriptor;
};

function getPreviousNodes(allNodes: NodeModelWithId[], currentNodeId: string) {
  const previousNodes: NodeModelWithIdAndStep[] = [];
  let parentNode: NodeModelWithId | undefined = allNodes.find(({ id }: { id: string }) => currentNodeId === id);

  while (
    (parentNode = allNodes.find(({ transitions }: { transitions: NodeTransitionList }) =>
      transitions?.some(({ targetNodeId }: { targetNodeId: string }) => targetNodeId === parentNode?.id)
    ))
  ) {
    if (
      [
        CloudFlowNodeType.MANUAL_TRIGGER,
        CloudFlowNodeType.TRIGGER,
        CloudFlowNodeType.ACTION,
        CloudFlowNodeType.FILTER,
        CloudFlowNodeType.CONDITION,
        CloudFlowNodeType.TRANSFORMATION,
        CloudFlowNodeType.DATETIME_TRANSFORM,
        CloudFlowNodeType.WEBHOOK_TRIGGER,
        CloudFlowNodeType.SYSTEM_EVENT_TRIGGER,
      ].includes(parentNode.type)
    ) {
      previousNodes.unshift(parentNode);
    }
  }

  return previousNodes;
}

function isNodeWithOutputModel(node: {
  id: string;
  name: string;
  outputModel: UnwrappedApiServiceModelDescriptor | null;
}): node is NodeWitOutputModel {
  return node.outputModel !== null;
}

const outputModelFetcher: OutputModelFetcher = {
  getOutputModelFromActionNode: (
    node: NodeModelWithId<CloudFlowNodeType.ACTION>
  ): Promise<UnwrappedApiServiceModelDescriptor | null> => getOutputModelByOperationPointer(node.parameters.operation),
  getOutputModelFromSystemEventTriggerNode: async (
    node: NodeModelWithId<CloudFlowNodeType.SYSTEM_EVENT_TRIGGER>
  ): Promise<WithModel<UnwrappedApiServiceModelDescriptor> | null> =>
    getOutputModelBySystemEventTriggerNode(node.parameters),
};

function ignoreCorruptedReferencedNodesExceptions(exception: unknown) {
  if (exception instanceof ReferencedNodeNotFoundException || exception instanceof ModelNotFoundException) {
    return null;
  }
  throw exception;
}

export function useReferenceableNodes(
  currentNodeId: string,
  selfReferencedNodes: boolean = false
): {
  referenceableNodes: NodeWitOutputModel[];
  referencedNodesIncludingCurrent: NodeWitOutputModel[];
  loading: boolean;
} {
  const { getNodes } = useReactFlow<Node<RFNode, CloudFlowNodeType>>();
  const [loading, setLoading] = useState(true);
  const [referenceableNodes, setReferenceableNodes] = useState<NodeWitOutputModel[]>([]);
  const { globalVariables, localVariables, isLoading: variablesLoading } = useVariableContext();
  const variables = useMemo(
    () => ({
      globalVariables,
      localVariables,
    }),
    [globalVariables, localVariables]
  );
  const [referencedNodesIncludingCurrent, setReferencedNodesIncludingCurrent] = useState<NodeWitOutputModel[]>([]);

  useEffect(() => {
    async function generateReferenceNodes() {
      try {
        setLoading(true);
        const allNodes = getNodes().map(
          (node): NodeModelWithIdAndStep => ({
            id: node.id,
            stepNumber: node.data.stepNumber,
            name: node.data.nodeData.name,
            parameters: node.data.nodeData.parameters!,
            transitions: node.data.nodeData.transitions,
            type: node.data.nodeData.type,
          })
        );

        const previousNodes = getPreviousNodes(allNodes, currentNodeId);
        const nodes = (
          await Promise.all(
            previousNodes.map(async (node: Pick<NodeModelWithIdAndStep, "id" | "name" | "type" | "stepNumber">) => ({
              id: node.id,
              name: node.name,
              type: node.type,
              stepNumber: node.stepNumber,
              outputModel: await getNodeOutputModel(outputModelFetcher, allNodes, node.id, variables).catch(
                ignoreCorruptedReferencedNodesExceptions
              ),
            }))
          )
        ).filter(isNodeWithOutputModel);
        setReferenceableNodes(nodes);

        if (selfReferencedNodes) {
          const currentNode = allNodes.find(({ id }) => id === currentNodeId);
          const outputModel = await getNodeOutputModel(outputModelFetcher, allNodes, currentNodeId, variables).catch(
            ignoreCorruptedReferencedNodesExceptions
          );
          if (outputModel && currentNode) {
            setReferencedNodesIncludingCurrent([
              ...nodes,
              {
                id: currentNode.id,
                name: currentNode.name,
                type: currentNode.type,
                stepNumber: currentNode.stepNumber ?? 0,
                outputModel,
              },
            ]);
          }
        }
      } finally {
        setLoading(false);
      }
    }

    if (!variablesLoading) {
      generateReferenceNodes();
    }
  }, [currentNodeId, getNodes, variables, variablesLoading, selfReferencedNodes]);

  const isLoadingNodes = loading || variablesLoading;

  return {
    referenceableNodes,
    referencedNodesIncludingCurrent: selfReferencedNodes ? (referencedNodesIncludingCurrent ?? []) : [],
    loading: isLoadingNodes,
  };
}
